// src/belediye/pages/halkMasasi/HalkMasasiCreateApplicationPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import belediyeApi from "../../services/belediyeApi";
import { normalizePhone10 } from "../../services/halkMasasiApi";

import {
  refreshCitizenProfileThunk,
  selectActiveCitizen,
  selectProfileLoading,
  selectProfileError,
  clearCreateState,

  // OTP flow
  preSubmitOtpAssistedApplicationThunk,
  resendAssistedOtpThunk,
  submitVerifiedAssistedApplicationThunk,
  resetOtpState,
  clearOtpError,
  selectOtpState,
} from "../../redux/slices/belediyeHalkMasasiSlice";

import { safeText, titleCaseTr } from "../../components/utils/textFormat";

import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  PaperClipIcon,
  PhoneIcon,
  TagIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
  SignalIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  LockClosedIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";

import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";

import "./HalkMasasiCreateApplicationPage.css";

// ----------------------------------------------------------------------------
// Debug
// ----------------------------------------------------------------------------
const DEBUG = String(process.env.REACT_APP_DEBUG_HALK_MASASI || "").trim() === "1";
const log = (...args) => DEBUG && console.log("[HalkMasasiCreateApplicationPage]", ...args);
const logError = (...args) => DEBUG && console.error("[HalkMasasiCreateApplicationPage]", ...args);

const BASE = "/yonetim/halk-masasi";

// ----------------------------------------------------------------------------
// Leaflet marker icon fix (CRA/webpack compatible, no external URL)
// ----------------------------------------------------------------------------
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------
const normalizeListPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const safeRound6 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Number(n.toFixed(6));
};

const humanFileSize = (bytes) => {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

const isValidLatLng = (lat, lng) => {
  const la = Number(lat);
  const lo = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return false;
  if (la < -90 || la > 90) return false;
  if (lo < -180 || lo > 180) return false;
  return true;
};

const pickCitizenId = (c) => {
  const id = c?.id ?? c?.citizen_id ?? c?.pk ?? null;
  return id != null ? String(id) : "";
};

const toDigits = (v) => String(v ?? "").replace(/\D+/g, "");
const validatePhone10 = (phone10) =>
  /^\d{10}$/.test(String(phone10 || "")) && String(phone10).startsWith("5");

const validateEmail = (email) => {
  const s = String(email || "").trim();
  if (!s) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

const maskPhone = (phone10) => {
  const p = String(phone10 || "").replace(/\D+/g, "");
  if (p.length !== 10) return String(phone10 || "");
  return `${p.slice(0, 3)} *** ** ${p.slice(-2)}`;
};

const formatRemaining = (totalSeconds) => {
  const n = Math.max(0, Number(totalSeconds || 0));
  const mm = String(Math.floor(n / 60)).padStart(2, "0");
  const ss = String(n % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

// ----------------------------------------------------------------------------
// UI: Toast
// ----------------------------------------------------------------------------
function Toast({ toast, onClose }) {
  if (!toast) return null;

  const Icon =
    toast.type === "success"
      ? CheckCircleIcon
      : toast.type === "info"
      ? ExclamationTriangleIcon
      : ExclamationTriangleIcon;

  return (
    <div className={`hm-toast ${toast.type}`} role="status" aria-live="polite">
      <Icon className="hm-toast-ic" aria-hidden="true" />
      <div className="hm-toast-body">
        <div className="hm-toast-title">{toast.title}</div>
        <div className="hm-toast-text">{toast.message}</div>
      </div>
      <button className="hm-toast-close" onClick={onClose} aria-label="Kapat">
        <XMarkIcon className="hm-toast-close-ic" />
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// UI: OTP Modal — Modern tasarım, 6 ayrı hücre, timer, resend
// ----------------------------------------------------------------------------
function OtpModal({
  open,
  phone10,
  code,
  onCodeChange,
  onVerify,
  onResend,
  onClose,
  expiresInSec,
  resendInSec,
  smsRemaining,
  loading,
  errorText,
}) {
  const inputRefs = useRef([]);

  // Modal açılınca ilk hücreye odaklan
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRefs.current[0]?.focus(), 100);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  // Kodu 6 karaktere normalize et, boş yerler " " değil "" olsun
  const rawCode = String(code || "");
  const digits = Array.from({ length: 6 }, (_, i) => rawCode[i] || "");

  const handleCellChange = (idx, val) => {
    const cleaned = val.replace(/\D/g, "").slice(-1);
    const arr = Array.from({ length: 6 }, (_, i) => rawCode[i] || "");
    arr[idx] = cleaned;
    onCodeChange(arr.join(""));
    if (cleaned && idx < 5) {
      setTimeout(() => inputRefs.current[idx + 1]?.focus(), 0);
    }
  };

  const handleCellKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = Array.from({ length: 6 }, (_, i) => rawCode[i] || "");
      if (arr[idx]) {
        arr[idx] = "";
        onCodeChange(arr.join(""));
      } else if (idx > 0) {
        arr[idx - 1] = "";
        onCodeChange(arr.join(""));
        inputRefs.current[idx - 1]?.focus();
      }
    }
    if (e.key === "ArrowLeft" && idx > 0) inputRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) inputRefs.current[idx + 1]?.focus();
    if (e.key === "Enter" && rawCode.trim().length === 6 && !loading) onVerify();
    if (e.key === "Escape" && !loading) onClose();
  };

  const handleCellPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    onCodeChange(pasted.padEnd(6, "").slice(0, 6).replace(/ /g, ""));
    const focusIdx = Math.min(pasted.length, 5);
    setTimeout(() => inputRefs.current[focusIdx]?.focus(), 0);
  };

  const codeComplete = rawCode.replace(/\s/g, "").length === 6;
  const canResend = resendInSec <= 0 && !loading && Number(smsRemaining || 0) > 0;

  // Timer bar — sol→sağ doluluk (kalan süre / max 300sn)
  const maxSec = 300;
  const timerPct = expiresInSec > 0 ? Math.min(100, (expiresInSec / maxSec) * 100) : 0;
  const timerDanger  = expiresInSec > 0 && expiresInSec <= 60;
  const timerWarning = expiresInSec > 60 && expiresInSec <= 120;

  return (
    <>
      {/* Backdrop */}
      <div
        className="hmca-otp-backdrop"
        onClick={loading ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="hmca-otp-wrap"
        role="dialog"
        aria-modal="true"
        aria-label="SMS doğrulama"
      >
        <div className="hmca-otp-card">

          {/* ── Başlık ── */}
          <div className="hmca-otp-head">
            <div className="hmca-otp-head-icon" aria-hidden="true">
              <DevicePhoneMobileIcon className="hmca-otp-head-icon-ic" />
            </div>

            <div className="hmca-otp-head-text">
              <div className="hmca-otp-head-title">SMS Doğrulama</div>
              <div className="hmca-otp-head-sub">
                <strong>{maskPhone(phone10)}</strong> numarasına 6 haneli kod gönderildi
              </div>
            </div>

            <button
              type="button"
              className="hmca-otp-head-close"
              onClick={onClose}
              disabled={loading}
              aria-label="Kapat"
            >
              <XMarkIcon />
            </button>
          </div>

          {/* ── Timer bar ── */}
          <div className="hmca-otp-timerbar-track" aria-hidden="true">
            <div
              className={`hmca-otp-timerbar-fill${timerDanger ? " is-danger" : timerWarning ? " is-warn" : ""}`}
              style={{ width: `${timerPct}%` }}
            />
          </div>

          {/* ── Body ── */}
          <div className="hmca-otp-body">

            {/* Hata */}
            {errorText ? (
              <div className="hmca-otp-err" role="alert">
                <ExclamationTriangleIcon className="hmca-otp-err-ic" aria-hidden="true" />
                <span>{errorText}</span>
              </div>
            ) : null}

            {/* 6 hücre */}
            <div className="hmca-otp-cells-wrap">
              <div className="hmca-otp-cells-label">Doğrulama Kodu</div>
              <div
                className="hmca-otp-cells"
                role="group"
                aria-label="6 haneli doğrulama kodu"
              >
                {[0, 1, 2, 3, 4, 5].map((idx) => (
                  <input
                    key={idx}
                    ref={(el) => (inputRefs.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    className={`hmca-otp-cell${digits[idx] ? " is-filled" : ""}${loading ? " is-loading" : ""}`}
                    value={digits[idx]}
                    onChange={(e) => handleCellChange(idx, e.target.value)}
                    onKeyDown={(e) => handleCellKeyDown(idx, e)}
                    onPaste={handleCellPaste}
                    onFocus={(e) => e.target.select()}
                    disabled={loading}
                    autoComplete={idx === 0 ? "one-time-code" : "off"}
                    aria-label={`${idx + 1}. hane`}
                  />
                ))}
              </div>
            </div>

            {/* Meta: süre + kalan SMS */}
            <div className="hmca-otp-meta">
              <div className={`hmca-otp-meta-pill${timerDanger ? " is-danger" : ""}`}>
                <ClockIcon className="hmca-otp-meta-ic" aria-hidden="true" />
                <span>
                  {expiresInSec > 0 ? formatRemaining(expiresInSec) : "Süre doldu"}
                </span>
              </div>

              {Number(smsRemaining || 0) > 0 ? (
                <div className="hmca-otp-meta-pill">
                  <DevicePhoneMobileIcon className="hmca-otp-meta-ic" aria-hidden="true" />
                  <span>Kalan hak: <strong>{smsRemaining}</strong></span>
                </div>
              ) : null}
            </div>
          </div>

          {/* ── Aksiyonlar ── */}
          <div className="hmca-otp-actions">
            <button
              type="button"
              className="hmca-otp-btn-resend"
              onClick={onResend}
              disabled={!canResend}
              title={
                resendInSec > 0
                  ? `${formatRemaining(resendInSec)} sonra tekrar gönderebilirsiniz`
                  : canResend ? "Kodu tekrar gönder" : "Hak kalmadı"
              }
            >
              <ArrowPathIcon className={`hmca-otp-btn-ic${loading && !codeComplete ? " hm-spin" : ""}`} aria-hidden="true" />
              {resendInSec > 0
                ? `Tekrar gönder (${formatRemaining(resendInSec)})`
                : "Tekrar Gönder"}
            </button>

            <button
              type="button"
              className={`hmca-otp-btn-verify${codeComplete && !loading ? " is-ready" : ""}`}
              onClick={onVerify}
              disabled={loading || !codeComplete}
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="hmca-otp-btn-ic hm-spin" aria-hidden="true" />
                  Doğrulanıyor…
                </>
              ) : (
                <>
                  <ShieldCheckIcon className="hmca-otp-btn-ic" aria-hidden="true" />
                  Doğrula ve Gönder
                </>
              )}
            </button>
          </div>

          {/* ── Footer ── */}
          <div className="hmca-otp-foot">
            <InformationCircleIcon className="hmca-otp-foot-ic" aria-hidden="true" />
            <span>
              Vatandaşın telefonuna SMS gönderildi. Kodu alıp buraya girerek başvuruyu tamamlayın.
            </span>
          </div>

        </div>
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------
// Leaflet: click-to-set marker
// ----------------------------------------------------------------------------
function ClickToSetMarker({ value, onChange, disabled }) {
  useMapEvents({
    click(e) {
      if (disabled) return;
      const lat = safeRound6(e?.latlng?.lat);
      const lng = safeRound6(e?.latlng?.lng);
      if (lat == null || lng == null) return;
      onChange?.({ lat, lng });
    },
  });

  if (!value) return null;
  return <Marker position={[value.lat, value.lng]} />;
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------
// react-leaflet: <MapContainer center> yalnız İLK mount'ta geçerlidir. İdari Sınırlar
// (boundary) async geldiğinde haritayı imperatif ortalar; yoksa eski merkezde kalırdı.
function RecenterOnBoundary({ view }) {
  const map = useMap();
  useEffect(() => {
    if (view?.center) {
      map.setView(view.center, view.zoom ?? map.getZoom(), { animate: false });
    }
  }, [view, map]);
  return null;
}

export default function HalkMasasiCreateApplicationPage() {
const dispatch = useDispatch();
const navigate = useNavigate();
const routeLocation = useLocation();
const { citizenId: routeCitizenId } = useParams();
  

  const boundCitizenId = useMemo(
    () => String(routeCitizenId || "").trim(),
    [routeCitizenId]
  );

  const isCitizenBoundMode = Boolean(boundCitizenId);
  // ---------------- Redux ----------------
  const activeCitizen = useSelector(selectActiveCitizen);
  const profileLoading = useSelector(selectProfileLoading);
  const profileError = useSelector(selectProfileError);

  const otpState = useSelector(selectOtpState);
  const {
    sessionToken: otpSessionToken,
    expiresAt: otpExpiresAt,
    resendAvailableAt: otpResendAvailableAt,
    smsRemaining: otpSmsRemaining,
    loading: otpLoading,
    verifyLoading: otpVerifyLoading,
    error: otpError,
    verifyError: otpVerifyError,
  } = otpState || {};

  const isBusy = Boolean(otpLoading || otpVerifyLoading);

  // ---------------- Lists ----------------
  const [units, setUnits] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  // ---------------- Local UI state ----------------
  const [toast, setToast] = useState(null);
  const [formError, setFormError] = useState("");
  const [fileError, setFileError] = useState("");

  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [clockNow, setClockNow] = useState(Date.now());

  // Kişi bilgileri
  const [person, setPerson] = useState({
    phone10: "",
    name: "",
    surname: "",
    email: "",
  });

  // Başvuru formu
  const [formData, setFormData] = useState({
    unit_id: "",
    neighborhood_id: "",
    address: "",
    subject: "",
    description: "",
    intake_note: "",
  });

  // Konum
  const [location, setLocation] = useState(null);
  const [gpsStatus, setGpsStatus] = useState("idle");

  // Dosyalar
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Double submit guard
  const submittingRef = useRef(false);

  const showToast = useCallback((type, title, message) => setToast({ type, title, message }), []);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!otpModalOpen) return;
    const t = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [otpModalOpen]);

  const otpExpiresInSec = useMemo(() => {
    if (!otpExpiresAt) return 0;
    const diff = Math.floor((new Date(otpExpiresAt).getTime() - clockNow) / 1000);
    return Math.max(0, diff);
  }, [otpExpiresAt, clockNow]);

  const otpResendInSec = useMemo(() => {
    if (!otpResendAvailableAt) return 0;
    const diff = Math.floor((new Date(otpResendAvailableAt).getTime() - clockNow) / 1000);
    return Math.max(0, diff);
  }, [otpResendAvailableAt, clockNow]);

  // Init
  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(clearCreateState());
    dispatch(resetOtpState());

    log("mount", {
      hasToken: Boolean(localStorage.getItem("accessToken")),
      role: (() => {
        try {
          const u = JSON.parse(localStorage.getItem("user") || "null");
          return u?.role || null;
        } catch {
          return null;
        }
      })(),
      hasActiveCitizen: Boolean(pickCitizenId(activeCitizen)),
    });

    return () => {
      dispatch(resetOtpState());
    };
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

// Prefill person safely by route mode
useEffect(() => {
  // Serbest /yeni-basvuru modunda eski activeCitizen kullanılmasın.
  if (!isCitizenBoundMode) {
    return;
  }

  const stateCitizen = routeLocation?.state?.prefillCitizen || null;
  const stateCitizenId = pickCitizenId(stateCitizen);
  const activeCitizenId = pickCitizenId(activeCitizen);

  let source = null;

  if (stateCitizenId && String(stateCitizenId) === String(boundCitizenId)) {
    source = stateCitizen;
  } else if (activeCitizenId && String(activeCitizenId) === String(boundCitizenId)) {
    source = activeCitizen;
  }

  if (!source) {
    setFormError(
      "Vatandaş bilgisi yüklenemedi. Lütfen sorgu ekranından vatandaşı tekrar seçerek işlem başlatın."
    );
    return;
  }

  const p10 = normalizePhone10(source?.phone_number || source?.phone || "");

  setPerson((prev) => ({
    ...prev,
    phone10: p10 || "",
    name: safeText(source?.name || "", ""),
    surname: safeText(source?.surname || "", ""),
    email: safeText(source?.email || "", ""),
  }));

  log("prefill from bound citizen", {
    boundCitizenId,
    sourceCitizenId: pickCitizenId(source),
    phone10: p10 || null,
  });
}, [activeCitizen, boundCitizenId, isCitizenBoundMode, routeLocation?.state]);

  // Fetch lists
  useEffect(() => {
    const run = async () => {
      try {
        log("fetch lists: units + neighborhoods");
        const [unitRes, neighRes] = await Promise.all([
          belediyeApi.get("/common/units/"),
          belediyeApi.get("/core/neighborhoods/"),
        ]);
        const u = normalizeListPayload(unitRes?.data);
        const n = normalizeListPayload(neighRes?.data);
        setUnits(u);
        setNeighborhoods(n);
        log("lists loaded", { units: u.length, neighborhoods: n.length });
      } catch (err) {
        logError("lists failed", err?.response?.status, err?.message);
        showToast("error", "Bağlantı hatası", "Birim/Mahalle listeleri alınamadı. Lütfen tekrar deneyin.");
      }
    };
    run();
  }, [showToast]);

  // Cleanup previews on unmount
  const cleanupPreviews = useCallback((arr) => {
    const list = Array.isArray(arr) ? arr : [];
    for (const item of list) {
      if (item?.isImage && item?.previewUrl) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      }
    }
  }, []);

  useEffect(() => {
    return () => cleanupPreviews(selectedFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Options
  const unitOptions = useMemo(() => {
    const arr = Array.isArray(units) ? units : [];
    return arr
      .map((u) => ({
        id: u?.id,
        name: titleCaseTr(safeText(u?.name || u?.unit_name || u?.title || "", "")),
      }))
      .filter((x) => x.id && x.name);
  }, [units]);

  const neighborhoodOptions = useMemo(() => {
    const arr = Array.isArray(neighborhoods) ? neighborhoods : [];
    return arr
      .map((n) => ({
        id: n?.id,
        name: titleCaseTr(safeText(n?.name || "", "")),
      }))
      .filter((x) => x.id && x.name);
  }, [neighborhoods]);

  const citizenId = useMemo(() => {
  if (isCitizenBoundMode) return boundCitizenId;
  return "";
}, [isCitizenBoundMode, boundCitizenId]);

  // ---------------- Handlers (person) ----------------
const handlePhoneChange = useCallback((e) => {
  if (isCitizenBoundMode) return;

  const raw = e?.target?.value ?? "";
  const digits = normalizePhone10(toDigits(raw));
  setPerson((p) => ({ ...p, phone10: digits }));
  setFormError("");
  setFileError("");
}, [isCitizenBoundMode]);

const handlePersonChange = useCallback((e) => {
  if (isCitizenBoundMode) return;

  const { name, value } = e?.target || {};
  if (!name) return;
  setPerson((p) => ({ ...p, [name]: value }));
}, [isCitizenBoundMode]);

  // ---------------- Handlers (form) ----------------
  const handleFormChange = useCallback((e) => {
    const { name, value } = e?.target || {};
    if (!name) return;

    if (name === "unit_id") {
      setFormData((p) => ({ ...p, unit_id: value, neighborhood_id: "" }));
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
  }, []);

  // ---------------- Location (GPS + map) ----------------
  // Harita başlangıç merkezi/zoom'u İdari Sınırlar'dan (belediye-özel) gelir →
  // farklı belediyede (Arsuz/Kırıkhan) doğru şehir açılır; yoksa güvenli fallback.
  const [boundaryView, setBoundaryView] = useState(null);
  useEffect(() => {
    let iptal = false;
    belediyeApi
      .get("/bm4/boundaries/default/")
      .then((res) => {
        if (iptal) return;
        const d = res?.data?.default;
        const lat = Number(d?.center_lat ?? d?.center?.lat);
        const lng = Number(d?.center_lng ?? d?.center?.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setBoundaryView({ center: [lat, lng], zoom: Number(d?.zoom_level) || null });
        }
      })
      .catch(() => {});
    return () => {
      iptal = true;
    };
  }, []);
  const defaultCenter = useMemo(
    () => boundaryView?.center || [36.5, 36.35],
    [boundaryView]
  );
  const defaultZoom = boundaryView?.zoom || 12;

  const setLocationSafe = useCallback((pos) => {
    const lat = safeRound6(pos?.lat);
    const lng = safeRound6(pos?.lng);
    if (lat == null || lng == null) return;
    if (!isValidLatLng(lat, lng)) return;
    setLocation({ lat, lng });
  }, []);

  const handleGps = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGpsStatus("error");
      showToast("error", "GPS yok", "Tarayıcınız konum özelliğini desteklemiyor.");
      return;
    }

    setGpsStatus("searching");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = safeRound6(pos?.coords?.latitude);
        const lng = safeRound6(pos?.coords?.longitude);
        if (lat == null || lng == null) {
          setGpsStatus("error");
          showToast("error", "Konum alınamadı", "Haritadan konum seçebilirsiniz.");
          return;
        }
        setLocationSafe({ lat, lng });
        setGpsStatus("fixed");
        showToast("success", "Konum alındı", "GPS ile konum seçildi.");
      },
      (err) => {
        logError("gps error", err?.message || err);
        setGpsStatus("error");
        showToast("error", "Konum alınamadı", "Haritadan konum seçebilirsiniz.");
      },
      { enableHighAccuracy: true, timeout: 9000, maximumAge: 0 }
    );
  }, [setLocationSafe, showToast]);

  const handleLocationClear = useCallback(() => {
    setLocation(null);
    setGpsStatus("idle");
  }, []);

  // ---------------- Files ----------------
  const handleFileChange = useCallback(
    (e) => {
      setFileError("");
      setFormError("");

      const files = Array.from(e?.target?.files || []);
      if (!files.length) return;

      const validTypes = ["image/jpeg", "image/png", "image/gif", "application/pdf"];
      const MAX_SIZE = 10 * 1024 * 1024;

      let imageCount = selectedFiles.filter((x) => x?.isImage).length;
      let pdfCount = selectedFiles.filter((x) => !x?.isImage).length;

      const merged = [...selectedFiles];

      for (const file of files) {
        if (merged.length >= 4) {
          setFileError("En fazla 4 adet dosya yükleyebilirsiniz (2 resim + 2 PDF).");
          break;
        }
        if (file.size > MAX_SIZE) {
          setFileError("Her bir dosya en fazla 10 MB olabilir.");
          continue;
        }
        if (!validTypes.includes(file.type)) {
          setFileError("Geçersiz dosya türü (JPEG, PNG, GIF, PDF).");
          continue;
        }

        const isImg = file.type.startsWith("image/");
        if (isImg && imageCount >= 2) {
          showToast("info", "Resim sınırı", "En fazla 2 resim ekleyebilirsiniz.");
          continue;
        }
        if (!isImg && pdfCount >= 2) {
          showToast("info", "Belge sınırı", "En fazla 2 PDF belge ekleyebilirsiniz.");
          continue;
        }

        const previewUrl = isImg ? URL.createObjectURL(file) : "";
        merged.push({ file, previewUrl, isImage: isImg });

        if (isImg) imageCount += 1;
        else pdfCount += 1;
      }

      setSelectedFiles(merged);

      try {
        e.target.value = "";
      } catch {}

      log("files merged", { total: merged.length, img: imageCount, pdf: pdfCount });
    },
    [selectedFiles, showToast]
  );

  const handleRemoveFile = useCallback(
    (index) => {
      const updated = [...selectedFiles];
      const item = updated[index];

      if (item?.isImage && item?.previewUrl) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      }

      updated.splice(index, 1);
      setSelectedFiles(updated);
    },
    [selectedFiles]
  );

  // ---------------- Validation ----------------
  const validateAll = useCallback(() => {
    const errs = [];
if (isCitizenBoundMode && !boundCitizenId) {
  errs.push("Vatandaş bağlantısı bulunamadı. Lütfen sorgu ekranından tekrar işlem başlatın.");
}
    const p10 = normalizePhone10(person.phone10);
    if (!validatePhone10(p10)) errs.push("Telefon numarası geçersiz. 10 hane ve 5XXXXXXXXX olmalıdır.");
    if (!safeText(person.name, "")) errs.push("Ad zorunludur.");
    if (!safeText(person.surname, "")) errs.push("Soyad zorunludur.");
    if (!validateEmail(person.email)) errs.push("E-posta formatı geçersiz.");

    if (!safeText(formData.unit_id, "")) errs.push("İlgili birim seçimi zorunludur.");
    if (!safeText(formData.subject, "")) errs.push("Başvuru konusu zorunludur.");
    if (!safeText(formData.description, "")) errs.push("Açıklama zorunludur.");

    if (location && !isValidLatLng(location.lat, location.lng)) {
      errs.push("Konum geçersiz. Haritadan tekrar seçin.");
    }

    return errs;
 }, [boundCitizenId, formData, isCitizenBoundMode, location, person]);

const validateCitizenContext = useCallback(() => {
  if (!isCitizenBoundMode) return "";

  if (!boundCitizenId) {
    return "Vatandaş bağlantısı bulunamadı. Lütfen sorgu ekranından tekrar işlem başlatın.";
  }

  const stateCitizen = routeLocation?.state?.prefillCitizen || null;
  const stateId = pickCitizenId(stateCitizen);
  const activeId = pickCitizenId(activeCitizen);

  // Eğer route state ile vatandaş geldiyse onu esas al.
  // activeCitizen eski kalmış olabilir; bu durumda boş yere formu kilitlemeyelim.
  if (stateId && String(stateId) !== String(boundCitizenId)) {
    return "Vatandaş bilgisi ile sayfa bağlantısı uyuşmuyor. Lütfen sorgu ekranından tekrar işlem başlatın.";
  }

  // Route state yoksa activeCitizen fallback olarak kontrol edilir.
  if (!stateId && activeId && String(activeId) !== String(boundCitizenId)) {
    return "Vatandaş bilgisi ile sayfa bağlantısı uyuşmuyor. Lütfen sorgu ekranından tekrar işlem başlatın.";
  }

  return "";
}, [activeCitizen, boundCitizenId, isCitizenBoundMode, routeLocation?.state]);

  const buildPayload = useCallback(() => {
    const payload = {
      unit_id: Number(formData.unit_id),
      subject: String(formData.subject || "").trim(),
      description: String(formData.description || "").trim(),
      neighborhood_id: formData.neighborhood_id ? Number(formData.neighborhood_id) : undefined,
      address: safeText(formData.address, "") ? String(formData.address).trim() : undefined,
      intake_note: safeText(formData.intake_note, "") ? String(formData.intake_note).trim() : null,
    };

    if (location) {
      payload.latitude = safeRound6(location.lat);
      payload.longitude = safeRound6(location.lng);
    }

  if (isCitizenBoundMode) {
  payload.citizen_id = Number(boundCitizenId);
} else {
  payload.citizen = {
    name: String(person.name || "").trim(),
    surname: String(person.surname || "").trim(),
    phone_number: `0${normalizePhone10(person.phone10)}`,
    email: String(person.email || "").trim() || null,
  };
}

    let imageCount = 0;
    let docCount = 0;

    for (const item of selectedFiles) {
      const file = item?.file;
      if (!file) continue;

      if (item?.isImage) {
        if (imageCount === 0) payload.image1 = file;
        else if (imageCount === 1) payload.image2 = file;
        imageCount += 1;
      } else {
        if (docCount === 0) payload.document1 = file;
        else if (docCount === 1) payload.document2 = file;
        docCount += 1;
      }
    }

    return payload;
  }, [boundCitizenId, formData, isCitizenBoundMode, location, person, selectedFiles]);

  const resetFormAfterSuccess = useCallback(() => {
    setFormData({
      unit_id: "",
      neighborhood_id: "",
      address: "",
      subject: "",
      description: "",
      intake_note: "",
    });
    setLocation(null);
    setGpsStatus("idle");
    cleanupPreviews(selectedFiles);
    setSelectedFiles([]);
    setOtpCode("");
    setOtpModalOpen(false);
    dispatch(resetOtpState());
  }, [cleanupPreviews, dispatch, selectedFiles]);

  // ---------------- OTP handlers ----------------
  const handleCloseOtpModal = useCallback(() => {
    if (otpVerifyLoading) return;
    setOtpModalOpen(false);
    setOtpCode("");
    dispatch(clearOtpError());
    dispatch(resetOtpState());
  }, [dispatch, otpVerifyLoading]);

  const handleResendOtp = useCallback(async () => {
    try {
      dispatch(clearOtpError());

      if (!otpSessionToken) {
        showToast("error", "Oturum bulunamadı", "Yeni başvuru gönderimi başlatın.");
        return;
      }

      await dispatch(resendAssistedOtpThunk({ sessionToken: otpSessionToken })).unwrap();
      showToast("success", "Yeni kod gönderildi", "Vatandaş telefonuna yeni doğrulama kodu gönderildi.");
    } catch (err) {
      const msg =
        (typeof err === "string" && err) ||
        err?.detail ||
        err?.message ||
        "Kod tekrar gönderilemedi.";
      showToast("error", "Gönderilemedi", msg);
    }
  }, [dispatch, otpSessionToken, showToast]);

  const handleVerifyOtp = useCallback(async () => {
    try {
      dispatch(clearOtpError());

      if (!otpSessionToken) {
        showToast("error", "Oturum bulunamadı", "Yeni başvuru gönderimi başlatın.");
        return;
      }

      if (String(otpCode || "").trim().length !== 6) {
        showToast("info", "Eksik kod", "Lütfen 6 haneli doğrulama kodunu girin.");
        return;
      }

      const response = await dispatch(
        submitVerifiedAssistedApplicationThunk({
          sessionToken: otpSessionToken,
          code: otpCode,
        })
      ).unwrap();

      const createdId =
        response?.applicationId ||
        response?.data?.application_id ||
        response?.data?.application?.id ||
        response?.data?.id ||
        null;

      showToast("success", "Başarılı", "Başvuru doğrulandı ve oluşturuldu.");

      if (citizenId) {
        dispatch(refreshCitizenProfileThunk({ citizenId: String(citizenId) }));
      }

      resetFormAfterSuccess();

      const toId = createdId ? String(createdId) : null;
      if (toId) {
        navigate(`${BASE}/basvuru/${toId}`, { replace: true });
      } else if (citizenId) {
        navigate(`${BASE}/vatandas/${citizenId}`, { replace: false });
      } else {
        navigate(`${BASE}/sorgu`, { replace: false });
      }
    } catch (err) {
      const msg =
        (typeof err === "string" && err) ||
        err?.detail ||
        err?.message ||
        "Kod doğrulanamadı.";
      showToast("error", "Doğrulama başarısız", msg);
    }
  }, [citizenId, dispatch, navigate, otpCode, otpSessionToken, resetFormAfterSuccess, showToast]);

  // ---------------- Submit ----------------
  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (submittingRef.current) return;
      submittingRef.current = true;

      try {
        setFormError("");
        setFileError("");
        setToast(null);
        dispatch(clearOtpError());

        const errs = validateAll();
if (errs.length) {
  const msg = errs[0];
  setFormError(msg);
  showToast("info", "Eksik / Hatalı bilgi", msg);
  return;
}

const contextError = validateCitizenContext();
if (contextError) {
  setFormError(contextError);
  showToast("error", "Vatandaş bağlantısı hatalı", contextError);
  return;
}

const payload = buildPayload();

        log("pre-submit payload summary", {
          citizenId: citizenId || null,
          hasCitizenObject: Boolean(payload.citizen),
          unit_id: payload.unit_id,
          hasLatLng: Boolean(payload.latitude != null && payload.longitude != null),
          hasFiles: Boolean(payload.image1 || payload.image2 || payload.document1 || payload.document2),
        });

        const response = await dispatch(preSubmitOtpAssistedApplicationThunk({ payload })).unwrap();

        // OTP açık → modal aç
        if (response?.otp?.sessionToken || response?.data?.session_token) {
          setOtpCode("");
          setOtpModalOpen(true);
          showToast(
            "success",
            "Kod gönderildi",
            "Vatandaş telefonuna gönderilen doğrulama kodunu girerek başvuruyu tamamlayın."
          );
          return;
        }

        // OTP kapalı → normal create response
        const createdId =
          response?.applicationId ||
          response?.data?.application_id ||
          response?.data?.application?.id ||
          response?.data?.id ||
          null;

        showToast("success", "Başarılı", "Başvuru oluşturuldu.");

        if (citizenId) {
          dispatch(refreshCitizenProfileThunk({ citizenId: String(citizenId) }));
        }

        resetFormAfterSuccess();

        const toId = createdId ? String(createdId) : null;
        if (toId) {
          const to = `${BASE}/basvuru/${toId}`;
          log("navigate created ->", to);
          navigate(to, { replace: true });
        } else if (citizenId) {
          navigate(`${BASE}/vatandas/${citizenId}`, { replace: false });
        } else {
          navigate(`${BASE}/sorgu`, { replace: false });
        }
      } catch (err) {
        logError("submit error", err?.response?.status, err?.message, err);
        const msg =
          err?.detail ||
          err?.message ||
          "Başvuru işlemi başlatılamadı. Lütfen bilgileri kontrol edip tekrar deneyin.";
        setFormError(String(msg));
        showToast("error", "Beklenmeyen hata", String(msg));
      } finally {
        submittingRef.current = false;
      }
    },
    [buildPayload, citizenId, dispatch, navigate, resetFormAfterSuccess, showToast, validateCitizenContext, validateAll]
  );
const goCreateForAnotherCitizen = useCallback(() => {
  navigate(`${BASE}/yeni-basvuru`, { replace: true });
}, [navigate]);
  // ---------------- Navigation ----------------
const goBack = useCallback(() => {
  if (isCitizenBoundMode && citizenId) {
    navigate(`${BASE}/vatandas/${citizenId}`, { replace: false });
    return;
  }

  navigate(`${BASE}/sorgu`, { replace: false });
}, [navigate, citizenId, isCitizenBoundMode]);
  // ---------------- Effective error ----------------
  const effectiveError = useMemo(() => {
    const local = String(formError || "").trim();
    if (local) return local;
    if (profileError) return String(profileError);
    return "";
  }, [formError, profileError]);

  // ---------------- Layout gate ----------------
  const canSubmit = useMemo(() => {
  if (isBusy) return false;
  const errs = validateAll();
  if (errs.length > 0) return false;

  const contextError = validateCitizenContext();
  return !contextError;
}, [isBusy, validateAll, validateCitizenContext]);

  // ---------------- Render ----------------
  return (
    <div className="hm-page hm-create-page" data-unit-selected={formData.unit_id ? "1" : "0"}>
      <div className="hm-shell hm-shell--full">
        {/* Page Bar */}
        <div className="hm-pagebar hm-pagebar--vca">
          <div className="hm-pagebar-left">
            <div className="hm-pagebar-head">
              <div className="hm-pagebar-badge" aria-hidden="true">
                <DocumentPlusIcon className="hm-pagebar-badge-ic" />
              </div>

              <div className="hm-pagebar-titlewrap">
                <div className="hm-pagebar-title">
  {isCitizenBoundMode ? "Seçili Vatandaş İçin Yeni Başvuru" : "Yeni Başvuru Ekle"}
</div>

<div className="hm-pagebar-sub">
  {isCitizenBoundMode
    ? "Bu başvuru yalnızca seçili vatandaş adına oluşturulur."
    : "Halk Masası üzerinden yeni vatandaş başvurusu oluşturun."}
</div>
              </div>
            </div>
          </div>

          <div className="hm-pagebar-actions">
            <button type="button" className="btn-md btn-md-ghost" onClick={goBack} disabled={isBusy}>
              <ArrowLeftIcon width={18} height={18} />
              Geri
            </button>
          </div>
        </div>

  

        {/* Error banner */}
        {effectiveError ? (
          <div className="hm-alert hm-alert--error" role="alert">
            <ExclamationTriangleIcon className="hm-alert-ic" aria-hidden="true" />
            <div className="hm-alert-text">{effectiveError}</div>
          </div>
        ) : null}

        {/* 2 Column Layout */}
        <form className="hmca-form" onSubmit={handleSubmit}>
          <div className="hmca-grid">
            {/* LEFT */}
            <div className="hmca-col">
              {/* Card: Person */}
              <section className="md-card hmca-card" aria-label="İletişim Bilgileri">
                <div className="hmca-card-title">
                  <UserIcon className="hmca-card-title-ic" aria-hidden="true" />
                  <span>İletişim Bilgileri</span>
                </div>

                <div className="hmca-fields">
{isCitizenBoundMode ? (
  <div className="hmca-bound-citizen-notice" role="note">
    <div className="hmca-bound-citizen-notice-main">
      <div className="hmca-bound-citizen-notice-icon" aria-hidden="true">
        <LockClosedIcon className="hmca-bound-citizen-notice-icon-svg" />
      </div>

      <div className="hmca-bound-citizen-notice-content">
        <div className="hmca-bound-citizen-notice-title">
          Başvuru seçili vatandaş adına oluşturulacak
        </div>

        <div className="hmca-bound-citizen-notice-sub">
          Kimlik ve iletişim alanları bu ekranda kilitlidir.
        </div>
      </div>
    </div>

    <button
      type="button"
      className="btn-md btn-md-ghost hmca-bound-citizen-notice-btn"
      onClick={goCreateForAnotherCitizen}
      disabled={isBusy}
    >
      Başka Vatandaş İçin Yeni Başvuru
    </button>
  </div>
) : null}

                  <div className="hmca-row hmca-row--2">
                    <div className="hmca-field">
                      <label className="hmca-label">
                        Ad <span className="hmca-req">*</span>
                      </label>
                      <div className="hmca-input-wrap">
                        <UserIcon className="hmca-input-ic" aria-hidden="true" />
                       <input
  className={`hmca-input ${isCitizenBoundMode ? "is-readonly" : ""}`}
  name="name"
  value={person.name}
  onChange={handlePersonChange}
  disabled={isBusy}
  readOnly={isCitizenBoundMode}
  placeholder="Ad"
/>
                      </div>
                    </div>

                    <div className="hmca-field">
                      <label className="hmca-label">
                        Soyad <span className="hmca-req">*</span>
                      </label>
                      <div className="hmca-input-wrap">
                        <UserIcon className="hmca-input-ic" aria-hidden="true" />
                     <input
  className={`hmca-input ${isCitizenBoundMode ? "is-readonly" : ""}`}
  name="surname"
  value={person.surname}
  onChange={handlePersonChange}
  disabled={isBusy}
  readOnly={isCitizenBoundMode}
  placeholder="Soyad"
/>
                      </div>
                    </div>
                  </div>

                  <div className="hmca-row hmca-row--2">
                    <div className="hmca-field">
                      <label className="hmca-label">
                        Telefon <span className="hmca-req">*</span>
                      </label>
                      <div className="hmca-input-wrap">
                        <PhoneIcon className="hmca-input-ic" aria-hidden="true" />
                      <input
  className={`hmca-input ${isCitizenBoundMode ? "is-readonly" : ""}`}
  name="phone10"
  value={person.phone10}
  onChange={handlePhoneChange}
  disabled={isBusy}
  readOnly={isCitizenBoundMode}
  placeholder="5XXXXXXXXX"
  inputMode="numeric"
  maxLength={10}
/>
                      </div>
                    </div>

                    <div className="hmca-field">
                      <label className="hmca-label">E-posta</label>
                      <div className="hmca-input-wrap">
                        <EnvelopeIcon className="hmca-input-ic" aria-hidden="true" />
                     <input
  className={`hmca-input ${isCitizenBoundMode ? "is-readonly" : ""}`}
  name="email"
  value={person.email}
  onChange={handlePersonChange}
  disabled={isBusy}
  readOnly={isCitizenBoundMode}
  placeholder="ornek@mail.com"
/>
                      </div>
                    </div>
                  </div>

                  {DEBUG ? (
                    <div className="hmca-debug">
                      <div>debug.citizenId={String(citizenId || "")}</div>
                      <div>debug.profileLoading={String(profileLoading)}</div>
                    </div>
                  ) : null}
                </div>
              </section>

              {/* Card: Application */}
              <section className="md-card hmca-card" aria-label="Başvuru Bilgileri">
                <div className="hmca-card-title">
                  <DocumentPlusIcon className="hmca-card-title-ic" aria-hidden="true" />
                  <span>Başvuru Bilgileri</span>
                </div>

                <div className="hmca-fields">
                  <div className="hmca-field">
                    <label className="hmca-label">
                      İlgili Birim <span className="hmca-req">*</span>
                    </label>
                    <div className="hmca-select-wrap">
                      <BuildingOffice2Icon className="hmca-input-ic" aria-hidden="true" />
                      <select
                        className="hmca-select"
                        name="unit_id"
                        value={formData.unit_id}
                        onChange={handleFormChange}
                        disabled={isBusy}
                        required
                      >
                        <option value="">Seçiniz</option>
                        {unitOptions.map((u) => (
                          <option key={String(u.id)} value={String(u.id)}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="hmca-field">
                    <label className="hmca-label">Mahalle</label>
                    <div className="hmca-select-wrap">
                      <MapPinIcon className="hmca-input-ic" aria-hidden="true" />
                      <select
                        className="hmca-select"
                        name="neighborhood_id"
                        value={formData.neighborhood_id}
                        onChange={handleFormChange}
                        disabled={isBusy}
                      >
                        <option value="">Seçiniz</option>
                        {neighborhoodOptions.map((n) => (
                          <option key={String(n.id)} value={String(n.id)}>
                            {n.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="hmca-field">
                    <label className="hmca-label">Adres</label>
                    <div className="hmca-input-wrap">
                      <MapPinIcon className="hmca-input-ic" aria-hidden="true" />
                      <input
                        className="hmca-input"
                        name="address"
                        value={formData.address}
                        onChange={handleFormChange}
                        disabled={isBusy}
                        placeholder="Adres"
                        maxLength={255}
                      />
                    </div>
                  </div>

                  <div className="hmca-field">
                    <label className="hmca-label">
                      Başvuru Konusu <span className="hmca-req">*</span>
                    </label>
                    <div className="hmca-input-wrap">
                      <TagIcon className="hmca-input-ic" aria-hidden="true" />
                      <input
                        className="hmca-input"
                        name="subject"
                        value={formData.subject}
                        onChange={handleFormChange}
                        disabled={isBusy}
                        placeholder="Kısa başlık"
                        maxLength={200}
                        required
                      />
                    </div>
                  </div>

                  <div className="hmca-field">
                    <label className="hmca-label">
                      Açıklama <span className="hmca-req">*</span>
                    </label>
                    <div className="hmca-textarea-wrap">
                      <ClipboardDocumentListIcon className="hmca-input-ic" aria-hidden="true" />
                      <textarea
                        className="hmca-textarea"
                        name="description"
                        value={formData.description}
                        onChange={handleFormChange}
                        disabled={isBusy}
                        placeholder="Detay"
                        rows={5}
                        required
                      />
                    </div>
                  </div>

                  <div className="hmca-field">
                    <label className="hmca-label">İlgili birime not ekleyin (isteğe bağlı)</label>
                    <div className="hmca-textarea-wrap">
                      <DocumentTextIcon className="hmca-input-ic" aria-hidden="true" />
                      <textarea
                        className="hmca-textarea"
                        name="intake_note"
                        value={formData.intake_note}
                        onChange={handleFormChange}
                        disabled={isBusy}
                        placeholder="Ek not (isteğe bağlı)"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </section>

              {DEBUG ? (
                <section className="md-card hmca-card" aria-label="Debug">
                  <div className="hmca-card-title">
                    <DocumentTextIcon className="hmca-card-title-ic" aria-hidden="true" />
                    <span>Debug</span>
                  </div>
                  <div className="hmca-fields">
                    <div className="hmca-debug">
                      <div>debug.otpSessionToken={String(otpSessionToken || "")}</div>
                      <div>debug.otpLoading={String(otpLoading)}</div>
                      <div>debug.otpVerifyLoading={String(otpVerifyLoading)}</div>
                      <div>debug.hasLocation={String(Boolean(location))}</div>
                      <div>debug.files={String(selectedFiles.length)}</div>
                    </div>
                  </div>
                </section>
              ) : null}
            </div>

            {/* RIGHT (MAP + FILES UNDER MAP) */}
            <div className="hmca-col hmca-col--location">
              {/* Map card */}
              <section className="md-card hmca-card" aria-label="Başvuru Konumu">
                <div className="hmca-card-title hmca-card-title--row">
                  <div className="hmca-card-title-left">
                    <MapPinIcon className="hmca-card-title-ic" aria-hidden="true" />
                    <span>Başvuru Konumu</span>
                  </div>

                  <div className="hmca-map-actions">
                    <button
                      type="button"
                      className={`hmca-map-btn ${gpsStatus === "fixed" ? "is-ok" : ""}`}
                      onClick={handleGps}
                      disabled={isBusy || gpsStatus === "searching"}
                      title="GPS ile konum bul"
                    >
                      {gpsStatus === "searching" ? (
                        <>
                          <ArrowPathIcon className="hmca-map-btn-ic hm-spin" aria-hidden="true" />
                          Konum alınıyor
                        </>
                      ) : (
                        <>
                          <SignalIcon className="hmca-map-btn-ic" aria-hidden="true" />
                          {gpsStatus === "fixed" ? "Konum alındı" : "Konum bul"}
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="hmca-map-btn hmca-map-btn--danger"
                      onClick={handleLocationClear}
                      disabled={isBusy || !location}
                      title="Konumu temizle"
                    >
                      <XMarkIcon className="hmca-map-btn-ic" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="hmca-fields">
                  <div className="hmca-help">Haritaya tıklayarak konumu seçin. (Opsiyonel)</div>

                  <div className="hmca-map">
                    <MapContainer
                      center={location ? [location.lat, location.lng] : defaultCenter}
                      zoom={location ? 16 : defaultZoom}
                      minZoom={11}
                      zoomSnap={0.5}
                      zoomDelta={0.5}
                      scrollWheelZoom
                      style={{ height: "100%", width: "100%" }}
                    >
                      <RecenterOnBoundary view={boundaryView} />
                      <TileLayer
                        attribution="&copy; OpenStreetMap"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <ClickToSetMarker value={location} onChange={setLocationSafe} disabled={isBusy} />
                    </MapContainer>
                  </div>

                  <div className="hmca-row hmca-row--2">
                    <div className="hmca-field">
                      <label className="hmca-label">Latitude</label>
                      <input className="hmca-input" value={location?.lat ?? ""} readOnly placeholder="Haritadan seçin" />
                    </div>
                    <div className="hmca-field">
                      <label className="hmca-label">Longitude</label>
                      <input className="hmca-input" value={location?.lng ?? ""} readOnly placeholder="Haritadan seçin" />
                    </div>
                  </div>
                </div>
              </section>

              {/* Files card */}
              <section className="md-card hmca-card" aria-label="Dosyalar">
                <div className="hmca-card-title">
                  <PaperClipIcon className="hmca-card-title-ic" aria-hidden="true" />
                  <span>Resim / Belge</span>
                </div>

                <div className="hmca-fields">
                  <div className="hmca-uploader">
                    <div className="hmca-uploader-row">
                      <div className="hmca-uploader-left">
                        <PaperClipIcon className="hmca-uploader-ic" aria-hidden="true" />
                        <div className="hmca-uploader-text">
                          <div className="hmca-uploader-title">Dosya ekle</div>
                          <div className="hmca-uploader-sub">En fazla 2 resim + 2 PDF (10 MB)</div>
                        </div>
                      </div>

                      <label className="hmca-uploader-btn">
                        <input
                          type="file"
                          multiple
                          onChange={handleFileChange}
                          disabled={isBusy}
                          className="hmca-file-input"
                          accept="image/jpeg,image/png,image/gif,application/pdf"
                        />
                        <ArrowUpTrayIcon className="hmca-uploader-btn-ic" aria-hidden="true" />
                        Seç
                      </label>
                    </div>

                    {selectedFiles.length ? (
                      <div className="hmca-previews">
                        {selectedFiles.map((item, idx) => (
                          <div className="hmca-preview" key={String(idx)}>
                            <div className="hmca-preview-thumb">
                              {item?.isImage ? (
                                <img src={item.previewUrl} alt="Önizleme" className="hmca-preview-img" />
                              ) : (
                                <div className="hmca-preview-pdf">
                                  <DocumentTextIcon className="hmca-preview-pdf-ic" aria-hidden="true" />
                                  <div className="hmca-preview-pdf-txt">PDF</div>
                                </div>
                              )}
                            </div>

                            <div className="hmca-preview-meta">
                              <div className="hmca-preview-name" title={item?.file?.name}>
                                {item?.file?.name || "Dosya"}
                              </div>
                              <div className="hmca-preview-size">{humanFileSize(item?.file?.size)}</div>
                            </div>

                            <button
                              type="button"
                              className="hmca-preview-del"
                              onClick={() => handleRemoveFile(idx)}
                              disabled={isBusy}
                              aria-label="Dosyayı kaldır"
                              title="Kaldır"
                            >
                              <TrashIcon className="hmca-preview-del-ic" aria-hidden="true" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {fileError ? <div className="hmca-uploader-err">{fileError}</div> : null}
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* ACTIONS */}
          <div className="hmca-actions">
            <div className="hmca-actions-left">
              <button type="submit" className="hmca-submit" disabled={!canSubmit || isBusy}>
                {otpLoading ? (
                  <>
                    <ArrowPathIcon className="hmca-submit-ic hm-spin" aria-hidden="true" />
                    İşlem başlatılıyor…
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="hmca-submit-ic" aria-hidden="true" />
                    Başvuru Oluştur
                  </>
                )}
              </button>

              {/* OTP aktifse uyarı notu */}
              <div className="hmca-sms-notice" aria-live="polite">
                <DevicePhoneMobileIcon className="hmca-sms-notice-ic" aria-hidden="true" />
                <span>
                  SMS doğrulama aktifse vatandaşın telefonuna{" "}
                  <strong>doğrulama kodu gönderilecektir</strong>
                </span>
              </div>
            </div>

            <button type="button" className="hmca-cancel" onClick={goBack} disabled={isBusy}>
              Vazgeç
            </button>
          </div>
        </form>

        {/* Toast */}
        <Toast toast={toast} onClose={() => setToast(null)} />

        {/* OTP Modal */}
        <OtpModal
          open={otpModalOpen}
          phone10={person.phone10}
          code={otpCode}
          onCodeChange={setOtpCode}
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
          onClose={handleCloseOtpModal}
          expiresInSec={otpExpiresInSec}
          resendInSec={otpResendInSec}
          smsRemaining={otpSmsRemaining}
          loading={Boolean(otpVerifyLoading || otpLoading)}
          errorText={otpVerifyError || otpError}
        />
      </div>
    </div>
  );
}